<template>
    <div class="card">
        <form class="form">
            <div v-if="isFetching" class="card-body">
              <div class="h-200px d-flex justify-content-center align-items-center">
                <div class="text-center">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">{{ $t("loadingText") }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="card-body">
                <RequiredText></RequiredText>
                <div class="row">
                    <div class="col-12 col-md-7 col-lg-7">
                        <div class="p-5 card shadow-sm rounded-2">
                            <div class="card-body">
                                <div class="row">
                                    <Input
                                        container-class="col-12 col-md-6 col-lg-6 form-group mb-5"
                                        :name="`event_title`"
                                        type="text"
                                        :label="$t('events.name_event')"
                                        labelClass="form-label fs-6 text-black"
                                        :inputClass="'form-control form-control-lg form-control-solid'"
                                        size="lg"
                                        :isRequired="true" 
                                        v-model="formData.title"
                                        :placeholder="$t('events.name_event_placeholder')"
                                    />

                                    <Input
                                        container-class="col-12 col-md-6 col-lg-6 form-group mb-5"
                                        :name="`event_location`"
                                        type="text"
                                        :label="$t('events.place_event')"
                                        labelClass="form-label fs-6 text-black"
                                        :inputClass="'form-control form-control-lg form-control-solid'"
                                        size="lg"
                                        :isRequired="true" 
                                        v-model="formData.place"
                                        :placeholder="$t('events.place_event_placeholder')"
                                    />
                                </div>

                                <CKEditor
                                    labelClass="form-label fs-6 text-black"
                                    :label="$t('events.description')"
                                    :id="`event_description`"
                                    :required="true"
                                    :content="formData.description"
                                    @text="setDescription"
                                />

                                <CustomSelect2
                                    container-class="form-group col-12 col-md-12 mt-5"
                                    :multiple="false"
                                    :show-search="true"
                                    :selectClass="'form-select form-control-sm form-select-solid'"
                                    :name="`time_zone_id`"
                                    :isRequired="true"
                                    :label="$t('events.gmt')"
                                    labelClass="form-label fs-6 text-black"
                                    :placeholder-text="$t('events.gmt_placeholder')"
                                    v-model="formData.timezone_id"
                                    :IsInputGroup="true"
                                >
                                    <option v-for="(timeZone, index) in timeZones" :key="index" :value="timeZone.id">
                                        ({{timeZone?.diff_from_gtm}}) {{ timeZone.name }}
                                    </option>
                                    <template #custom>
                                        <div class="icon-wrapper">
                                        <i class="fas fa-globe text-primary" ></i>
                                        </div>
                                    </template>
                                </CustomSelect2>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-md-5 col-lg-5">
                        <div class="p-5 card shadow-sm rounded-2">
                            <h4 class="mb-5">{{ $t('events.select_dates_text') }}</h4>
                            <DatePikerInline 
                                name="date-picker-event-id"
                                dateFormat="dd-MM-yyyy"
                                @handleDateClick="addDate"
                                :selectDates="selectDates"
                            />
                            <div class="mt-5 card shadow-sm" v-for="(day, index) in formData.days" :key="index">
                                <div class="d-flex justify-content-between align-items-center px-5 py-2">
                                    <div class="col-12 col-md-3 col-lg-3">
                                        <span class="mb-5">{{ $t('events.day') }} {{ parseInt(index)+1 }}</span>
                                        <h4 class="text-primary">{{day.date}}</h4>
                                    </div>
                                    <div class="d-flex justify-content-center align-items-center col-12 col-md-9 col-lg-9">
                                        <div class="me-5 col-6">
                                            <span class="mb-3">{{ $t('events.start_hour') }}</span>
                                            <Input
                                            :name="'start_hour'"
                                            type="time"
                                            v-model="day.startHour"
                                            />
                                        </div>
                                        <div class="col-6">
                                            <span class="mb-3">{{ $t('events.end_hour') }}</span>
                                            <Input
                                            :name="'end_hour'"
                                            type="time"
                                            v-model="day.endHour"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card-footer">
                <div class="d-flex justify-content-end">
                    <Button v-if="$can(abilities.CAN_UPDATE_EVENT)" btn-class="btn-sm me-3" type="submit" @click="submitForm" id="submit_btn" btn-text="button.save" size="7px 30px" :is-loading="isLoading"></Button>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
    import {ref, onMounted, computed, watch} from 'vue'
    import CKEditor from "@/components/CKEditor.vue";
    import Button from "@/components/Button";
    import Input from "@/components/Input.vue";
    import {useStore} from "vuex";
    import CustomSelect2 from "@/components/CustomSelect2.vue";
    import RequiredText from "@/components/RequiredText.vue"
    import InputFileIcon from "@/components/InputFileIcon"
    import CheckboxInput from '@/components/CheckboxInput.vue';
    import DatePikerInline from '@/components/DatePikerInline.vue';
    import abilities from '@/composables/abilities';
    import {useRoute} from 'vue-router'
    import Img from "@/components/Img.vue";

    const emits = defineEmits(["load"]);
    
    const props = defineProps({
        detail: {
            type: Object, 
            default: {}
        }
    })

    const store = useStore()
    const route = useRoute()

    const isFetching  = ref(true)
    const isLoading  = ref(false)
    const formData = ref(props.detail);

    const selectDates = ref([])

    const timeZones = computed(() => store.getters["appointments/getTimeZones"])

    watch(() => props.detail, (value) => {
        if(value){
            formData.value = props.detail;
            props.detail?.days?.forEach((item) => {
                selectDates.value.push(item.date)
            });
        }
    }, { deep: true });

    watch(() => route.query.tab, (value) => {
        loadData()
    });
    
    function setDescription(text) {
        formData.value.description = text
    }

    function addDate(date) {
        const dayData = formData.value.days?.find(item => item.date == date)
        if (dayData) {
            if(dayData.hour_id){
                formData.value.remove.push(dayData.hour_id)
            }
            formData.value.days = formData.value.days?.filter(item => item.date != date)
        } else {
            let nbr = formData.value.days?.length+1
            const sortedDates = [...formData.value.days, {
                id: nbr,
                hour_id: null,
                date: date,
                startHour: '',
                endHour: ''
            }].sort((a, b) => {
            const d1 = new Date(a.date.split('-').reverse().join('-'));
            const d2 = new Date(b.date.split('-').reverse().join('-'));
            return d1 - d2;
            });
            formData.value.days = sortedDates
        }
    }
    
    function submitForm() {
        isLoading.value = true;
        store
            .dispatch('events/updateEvent', formData.value)
            .then(() => {
                isLoading.value = false;
                store.dispatch('events/getEventDetail', route.params.id)
                //emits('load')
                window.toastr["success"](i18n.global.t('events.updated_successfully'));
            })
            .catch((e) => {

                isLoading.value = false;
            });
    }

    function loadData() {
      isFetching.value = true
        if(route.query.tab === 'tab-information' || !route.query.tab) {
            store.dispatch('appointments/loadTimeZones')
            const root = document.documentElement;
            if(root) root.style.setProperty('--dp-menu-min-width', '420px');
            if(props.detail){
                formData.value = props.detail;
                props.detail?.days?.forEach((item) => {
                    selectDates.value.push(item.date)
                });
            }
        }
      isFetching.value = false
    }

    onMounted(() => {
        loadData()
    })
</script>

<style scoped>
    .icon-wrapper {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .icon-wrapper i {
        position: relative;
        font-size: 1.5em;
    }

    .icon-wrapper .icon-text {
        position: absolute;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 9px;
        color: white;
        margin-top: 8px;
    }
</style>